import { Alert, Box, Button, CircularProgress, FormControl, MenuItem, Select, Typography } from "@mui/material";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../../components/common/toolbarStyles";
import axios, { AxiosResponse } from "axios";
import { FormEvent, useEffect, useState } from "react";
import { openSession, submitAttendnace } from "../../shared/AttendanceAPI";
import { FacultyNoGradesMessage, useFacultyHasNoGrades } from "../../shared/FacultyNoGrades";
import { GradeSectionComponent } from "../../shared/GradeSectionComponent";
import { MaterialUITable2 } from "../../shared/MaterialUITable2";
import { SharedDialog } from "../../shared/SharedDialog";
import { AttendanceDTO, AttendanceState, AttendanceViewState, MaterialUITableDataItem, Student, StudentState, ViewState } from "../../shared/types";
import { getLoginId } from "../../user/UserService";
import { useAttendancePageTabs } from "./AttendancePageTabs";
import { StudentMessageStatus } from "./StudentMessageStatus";

/** Students attendance: Mark Attendance and Message Status tabs. */
export const Attendance = () => {
  const [tab, tabs] = useAttendancePageTabs();
  return <Box sx={{ mt: 2, mr: 2 }}>{tab === "messages" ? <StudentMessageStatus tabs={tabs} /> : <MarkStudentAttendance tabs={tabs} />}</Box>;
};

const MarkStudentAttendance = ({ tabs }: { tabs: React.ReactNode }) => {
  console.log("In Attendance");

  const loggedInUserId = getLoginId();
  const noGrades = useFacultyHasNoGrades();
  // State
  const [viewState, setViewState] = useState<AttendanceViewState>(ViewState.IDLE);
  const [students, setStudents] = useState<Student[]>([]);
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");

  const [studentsAttendanceMUITableData, setStudentsAttendanceMUITableData] = useState<MaterialUITableDataItem[]>();
  const [sharedDialogState, setSharedDialogState] = useState(false);
  const [sharedDialogMessage, setSharedDialogMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  // from the backend: "Attendance saved. Messages are being sent." / "Attendance saved."
  const [submittedMessage, setSubmittedMessage] = useState("");
  // "Five – A": the class the shown result/message is for (the filters may have changed since)
  const [searchedClass, setSearchedClass] = useState("");

  useEffect(() => {
    console.log("In useEffect []");
    //This was complaining React Hook useEffect has a missing dependency: 'loadFacultyGrades'. Either include it or remove the dependency array
    console.log("Exiting useEffect []");
  }, []); //This executes only once

  //LEARNING async await
  //Why must the function containing await be marked async?
  // Because await pauses execution, and only an async function provides JavaScript with a Promise-based pause/resume context to do that without blocking the event loop.
  const getStudents = async (e: FormEvent<HTMLFormElement>) => {
    console.log("in getStudents");
    e.preventDefault()
    //TODO clear state before api call

    if (grade !== undefined && section !== undefined) {
      setSearchedClass(`${grade} – ${section}`);
      try {
        setViewState(ViewState.LOADING);
        let response: AxiosResponse = await openSession({ grade: grade, section: section, facultyLoginId: loggedInUserId });

        console.log("In getStudents::then");
        if (response.status === 200) {
          console.log("In getStudents::then 200");
          let students2: Student[] = response.data;
          if (students2.length > 0) {
            //Material Data Table Data
            let studentsAttendanceTableData: MaterialUITableDataItem[] = students2.map((std, index) => ({
              Name: std.name,
              "Guardian Name": std.guardian.name,
              Status: (
                <FormControl size="small" sx={{ ...toolbarFieldsSx, minWidth: 64 }}>
                  {/* same 32px / 14px size as the Grade and Section filters in the top bar */}
                  <Select
                    labelId="status-select-label"
                    id={std.name + ":" + std.admissionNo}
                    name={std.name + ":" + std.admissionNo}
                    defaultValue={"P"}
                    sx={{ height: 32, fontSize: 14 }}
                  >
                    {["P", "A", "L"]?.map((status: string) => (
                      <MenuItem value={status} key={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            }));
            setStudentsAttendanceMUITableData(studentsAttendanceTableData);

            setStudents(students2);
            setViewState(AttendanceState.REQUIRED);
          } else {
            console.log("In getStudents::then 204");
            setViewState(StudentState.NOT_FOUND);
          }
        } else if (response.status === 204) {
          setViewState(StudentState.NOT_FOUND);
        }
      } catch (error) {

        if (axios.isAxiosError(error)) {
          console.log(error?.response?.data);
          setErrorMessage(error.response?.data);
        } else {
          console.log("An unknown error occurred:", error);
          setErrorMessage("An unknown error occurred");
        }
        setViewState(ViewState.ERROR);
      }
    } else {
      alert("Invalid data");
    }
  };

  const submitAttendance = async (e: React.FormEvent<HTMLFormElement>) => {
    console.log("submitAttendance");
    e.preventDefault();
    //It do not wait here
    let formData = new FormData(e.currentTarget);
    let attendanceData: AttendanceDTO[] = getAtendences(formData);
    try {
      const response = await submitAttendnace({ grade: grade, section: section, facultyLoginId: loggedInUserId, data: attendanceData });
      setSubmittedMessage(response.data?.message ?? "");
      setViewState(AttendanceState.SUBMITTED);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(error?.response?.data);
        setErrorMessage(error.response?.data);
      } else {
        console.log("An unknown error occurred:", error);
        setErrorMessage("An unknown error occurred");
      }
      setViewState(ViewState.ERROR);
    }
  };

  /**
   * The best way to get dynamic table form data is
   * -----1. Use useRef as below.
   * -----2. either use new FormData
   * @param formData
   */
  const getAtendences = (formData: any): AttendanceDTO[] => {
    let attendances: AttendanceDTO[] = [];
    students.forEach((student) => {
      let status: string = formData.get(student.name + ":" + student.admissionNo)?.toString();
      if (status === "A") status = "Absent";
      else if (status === "P") status = "Attend";
      else if (status === "L") status = "Late";
      attendances.push({ name: student.name, admissionNo: student.admissionNo!, attendance: status });
    });
    return attendances;
  };

  return (
    <>
      <SharedDialog
        message={sharedDialogMessage}
        dialogState={sharedDialogState}
        setDialogState={(value: boolean) => setSharedDialogState(value)}
        proceedButtonRequired={false}
        cancelOptionText="Ok"
      />
      <Box>
        <PageTopBar
          left={tabs}
          right={
            !noGrades && (
              <Box component="form" display="flex" alignItems="center" gap={1} flexWrap="wrap" onSubmit={getStudents} sx={toolbarFieldsSx}>
                <GradeSectionComponent
                  grade={grade}
                  setGrade={setGrade}
                  section={section}
                  setSection={setSection}
                  inline
                />
                <Button type="submit" variant="outlined" size="small" sx={toolbarButtonSx}>
                  Get
                </Button>
              </Box>
            )
          }
        />

        {noGrades ? (
          <FacultyNoGradesMessage />
        ) : viewState === ViewState.LOADING ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : viewState === AttendanceState.REQUIRED ? (
          <MaterialUITable2 data={studentsAttendanceMUITableData} submitMethod={submitAttendance} insideForm={true} />
        ) : viewState === AttendanceState.SUBMITTED ? (
          <Alert severity="success">
            Attendance for {searchedClass} has been submitted. {submittedMessage}
            {submittedMessage.includes("Messages") && " Check the Message Status tab for each student's SMS."}
          </Alert>
        ) : viewState === ViewState.ERROR ? (
          // e.g. "attendance already submitted / in progress" comes back from the backend as an error message
          <Alert severity="warning">{errorMessage || "Something went wrong. Please try again."}</Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {viewState === AttendanceState.ALREADY_SUBMITTED
              ? `Attendance for ${searchedClass} has already been submitted today.`
              : viewState === StudentState.NOT_FOUND
                ? `No students in ${searchedClass}.`
                : "Select grade and section, then click Get to mark attendance."}
          </Typography>
        )}
      </Box>
    </>
  );
};
